/**
 * The scratch source manifest.
 *
 * Generated stabs are always available — they are rendered at runtime and carry
 * no licence question at all. Recorded sources are a separate, opt-in class:
 * "oh-yeah" is the classic battle-record vocal and this repo does not ship it,
 * does not fetch it, and does not assert a licence for it.
 *
 * The entry stays disabled until the user drops a file they are entitled to use
 * at public/audio/scratch/oh-yeah.wav AND fills in provenance. `enableRecorded`
 * is the only way to flip it, and it refuses without both.
 */

import type { StabId } from './stabs';
import { STAB_IDS, STAB_LABELS } from './stabs';

export interface ScratchSource {
  id: string;
  label: string;
  kind: 'generated' | 'recorded';
  /** Only set for recorded sources; generated ones render in-process. */
  file: string | null;
  enabled: boolean;
  provenance: { source: string; licence: string } | null;
  /** Shown in the UI when the source is disabled. */
  note?: string;
}

export const SCRATCH_MANIFEST: ScratchSource[] = [
  ...STAB_IDS.map((id: StabId): ScratchSource => ({
    id,
    label: STAB_LABELS[id],
    kind: 'generated',
    file: null,
    enabled: true,
    provenance: { source: 'generated in-process by src/audio/scratch/stabs.ts', licence: 'n/a — synthesised' },
  })),
  {
    id: 'oh-yeah',
    label: 'OH YEAH',
    kind: 'recorded',
    file: 'audio/scratch/oh-yeah.wav',
    enabled: false,
    provenance: null,
    note: 'Disabled. Add a licensed or self-recorded public/audio/scratch/oh-yeah.wav and its provenance to enable.',
  },
];

export interface ScratchIssue { id: string; message: string }

export function validateScratchManifest(sources: readonly ScratchSource[]): ScratchIssue[] {
  const issues: ScratchIssue[] = [];
  const seen = new Set<string>();
  for (const s of sources) {
    if (seen.has(s.id)) issues.push({ id: s.id, message: 'duplicate id' });
    seen.add(s.id);
    if (s.kind === 'recorded' && s.enabled && !s.file)
      issues.push({ id: s.id, message: 'enabled recorded source has no file' });
    if (s.enabled && !s.provenance)
      issues.push({ id: s.id, message: 'enabled source has no provenance' });
    if (s.kind === 'generated' && s.file)
      issues.push({ id: s.id, message: 'generated source must not reference a file' });
  }
  return issues;
}

/** The only door. Refuses a recorded source without a file and full provenance. */
export function enableRecorded(
  source: ScratchSource,
  file: string,
  provenance: { source: string; licence: string },
): ScratchSource {
  if (!file.trim()) throw new Error('a recorded source needs a file path');
  if (!provenance.source.trim() || !provenance.licence.trim())
    throw new Error('a recorded source needs both a source and a licence');
  return { ...source, file, provenance, enabled: true, note: undefined };
}

export const availableSources = (m: readonly ScratchSource[]): ScratchSource[] => m.filter((s) => s.enabled);
